import { NavLink, useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";
import { useRef, useEffect, useState } from "react";
import clsx from "clsx";
import { m, AnimatePresence } from "framer-motion";
import { useSidebarStore } from "../../store/sidebar.store";
import { NAV_ICONS } from "../../components/icons/navIcons";
import { type NavItem } from "../../app/nav.config";
import { useFilteredNavItems } from "../../app/useFilteredNavItems";
import { useTranslation } from "react-i18next";
import { useNotificationStore } from "../../store/notification.store";
import { LogOut, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuth } from "../../auth/AuthProvider";
import { ConfirmModal } from "../common/ConfirmModal";
import { TruncatedText } from "../common/TruncatedText";

function itemContainsPath(item: NavItem, pathname: string): boolean {
  // A group is reached only through its children: its own path is a
  // prefix that other groups' pages may share (Receivable Templates
  // lives under /receivables-engine/… but belongs to Library).
  if (item.children && item.children.length > 0) {
    return item.children.some((child) => itemContainsPath(child, pathname));
  }
  if (item.path === "/") return pathname === "/";
  if (item.alsoMatches?.some((pattern) => pattern.test(pathname))) return true;
  return pathname === item.path || pathname.startsWith(item.path + "/");
}

function NavChildren({
  items,
  collapsed,
  openMenus,
  toggleMenu,
  location,
  t,
}: {
  items: NavItem[];
  collapsed: boolean;
  openMenus: string[];
  toggleMenu: (path: string) => void;
  location: Location;
  t: (key: string) => string;
}) {
  return (
    <>
      {items.map((child) => {
        const ChildIcon = child.icon;

        if (child.children && child.children.length > 0) {
          const isMenuOpen = openMenus.includes(child.path);
          const isActive = child.children.some((grandchild) =>
            itemContainsPath(grandchild, location.pathname),
          );

          return (
            <div key={child.path}>
              <button
                type="button"
                onClick={() => toggleMenu(child.path)}
                aria-expanded={isMenuOpen}
                className={clsx(
                  "group flex h-9 w-full items-center justify-between gap-2 rounded-lg px-2.5 text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))] focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--card))]",
                  isActive
                    ? "font-medium text-[rgb(var(--text-accent))]"
                    : "text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--card-muted))] hover:text-[rgb(var(--text-primary))]",
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <ChildIcon size={15} className="shrink-0" />
                  <TruncatedText text={t(child.labelKey as any)} />
                </span>
                <m.span
                  animate={{ rotate: isMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <NAV_ICONS.chevron size={12} />
                </m.span>
              </button>

              <AnimatePresence initial={false}>
                {isMenuOpen && (
                  <m.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="ml-[18px] mt-0.5 flex flex-col gap-0.5 overflow-hidden border-l border-[rgb(var(--border))] pl-2"
                  >
                    <NavChildren
                      items={child.children}
                      collapsed={collapsed}
                      openMenus={openMenus}
                      toggleMenu={toggleMenu}
                      location={location}
                      t={t}
                    />
                  </m.div>
                )}
              </AnimatePresence>
            </div>
          );
        }

        // Exact matching is only needed when a sibling lives under this
        // path (e.g. /contracts vs /contracts/legacy-list); otherwise nested
        // pages such as /admin/licensing/organisations/:id should keep
        // their menu item highlighted.
        const hasNestedSibling = items.some(
          (other) =>
            other.path !== child.path &&
            other.path.startsWith(child.path + "/"),
        );

        const alsoActive = !!child.alsoMatches?.some((pattern) =>
          pattern.test(location.pathname),
        );

        return (
          <NavLink
            key={child.path}
            to={child.path}
            end={hasNestedSibling}
            className={({ isActive: routeActive }) =>
              clsx(
                "group relative flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))] focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--card))]",
                routeActive || alsoActive
                  ? "bg-[rgb(var(--text-accent))]/10 font-semibold text-[rgb(var(--text-accent))]"
                  : "text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--card-muted))] hover:text-[rgb(var(--text-primary))]",
              )
            }
          >
            {({ isActive: routeActive }) => {
              const isActive = routeActive || alsoActive;
              return (
                <>
                  {/* Marks the active page on the tree guide line. */}
                  {isActive && !collapsed && (
                    <span
                      aria-hidden
                      className="absolute -left-[9.5px] top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-[rgb(var(--accent-fill))]"
                    />
                  )}
                  <ChildIcon
                    size={15}
                    className={clsx(
                      "shrink-0 transition-colors",
                      isActive
                        ? "text-[rgb(var(--text-accent))]"
                        : "text-[rgb(var(--text-muted))] group-hover:text-[rgb(var(--text-secondary))]",
                    )}
                  />
                  <TruncatedText text={t(child.labelKey as any)} />
                </>
              );
            }}
          </NavLink>
        );
      })}
    </>
  );
}

/**
 * The label beside an icon in the collapsed rail. Inside the scrolling nav it
 * is `fixed` (an overflow-y container would clip an absolute one); `anchored`
 * places it beside its own button instead, for the pinned footer, which does
 * not scroll.
 */
function CollapsedTooltip({
  label,
  anchored = false,
}: {
  label: string;
  anchored?: boolean;
}) {
  return (
    <span
      className={clsx(
        "pointer-events-none z-[9999] whitespace-nowrap rounded-lg bg-[rgb(var(--text-primary))] px-2.5 py-1.5 text-xs font-medium text-[rgb(var(--bg))] opacity-0 shadow-lg transition-all duration-150 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100",
        anchored
          ? "absolute left-full top-1/2 ml-2 -translate-x-1.5 -translate-y-1/2"
          : "fixed left-[64px] translate-x-[-6px]",
      )}
    >
      {label}
    </span>
  );
}

export function Sidebar() {
  const { width, collapsed, setWidth, toggle, open, toggleOpen } =
    useSidebarStore();

  const { t } = useTranslation("navigation");
  const { t: tAuth } = useTranslation("auth");
  const { logout } = useAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const location = useLocation();
  const resizing = useRef(false);
  // The bell in the header already keeps this current; the menu item just
  // reads the same store so the count is visible without the header too.
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount);
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [flyoutTop, setFlyoutTop] = useState<number | null>(null);
  const asideRef = useRef<HTMLElement | null>(null);
  /**
   * The flyout is `position: fixed`, but the sidebar carries a transform
   * (translate-x for the mobile drawer), and a transformed ancestor becomes
   * the containing block for fixed children. So `top` is measured from the
   * sidebar, not the viewport — using the button's viewport top put the
   * flyout one header-height below the item it belongs to.
   */
  const flyoutTopFor = (el: Element) =>
    el.getBoundingClientRect().top -
    (asideRef.current?.getBoundingClientRect().top ?? 0);
  const topButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  /**
   * NEW SECURE NAV FILTERING
   */
  const filteredNav = useFilteredNavItems();
  const topLevelPaths = new Set(filteredNav.map((item) => item.path));
  // The nav fills in after the profile and licence load, so a group that
  // only appears then (Admin is admin-only) must still auto-open on refresh.
  // A stable key of the visible paths lets the effect below react to that
  // without re-running on every render.
  const navKey = (() => {
    const paths: string[] = [];
    const walk = (items: typeof filteredNav) =>
      items.forEach((i) => {
        paths.push(i.path);
        if (i.children) walk(i.children);
      });
    walk(filteredNav);
    return paths.join("|");
  })();

  const isChildRouteActive = (item: (typeof filteredNav)[number]) => {
    return Boolean(
      item.children &&
      item.children.some((child) => itemContainsPath(child, location.pathname)),
    );
  };

  const toggleMenu = (
    path: string,
    e?: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const isTopLevel = topLevelPaths.has(path);

    setOpenMenus((prev) => {
      const isOpen = prev.includes(path);

      // Only one top-level flyout can be open at a time while collapsed,
      // otherwise flyouts overlap since they're positioned outside doc flow.
      if (collapsed && isTopLevel) {
        return isOpen ? [] : [path];
      }

      return isOpen ? prev.filter((p) => p !== path) : [...prev, path];
    });

    if (collapsed && isTopLevel && e) {
      setFlyoutTop(flyoutTopFor(e.currentTarget));
    }
  };

  useEffect(() => {
    if (window.innerWidth < 768 && open) {
      toggleOpen();
    }
  }, [location.pathname]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizing.current) return;
      setWidth(e.clientX);
    };

    const onMouseUp = () => {
      resizing.current = false;
      document.body.style.cursor = "default";
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [setWidth]);

  useEffect(() => {
    const toOpen: string[] = [];
    const walk = (items: typeof filteredNav) => {
      items.forEach((item) => {
        if (item.children && itemContainsPath(item, location.pathname)) {
          toOpen.push(item.path);
          walk(item.children);
        }
      });
    };
    walk(filteredNav);

    if (collapsed) {
      // Collapsed, a group's children open as a floating flyout over the
      // page. Opening it for the active section on every navigation left it
      // sitting on top of the content just navigated to (and it never went
      // away on its own). Navigating now closes any flyout instead; the
      // active section is still marked by its tinted icon.
      setOpenMenus([]);
      return;
    }
    if (toOpen.length) {
      setOpenMenus((prev) =>
        toOpen.every((p) => prev.includes(p))
          ? prev
          : Array.from(new Set([...prev, ...toOpen])),
      );
    }
  }, [location.pathname, collapsed, navKey]);

  // Collapsed flyouts float over the page, so they close like a menu: on a
  // click anywhere outside the sidebar, or on Escape.
  const flyoutOpen = collapsed && openMenus.length > 0;
  useEffect(() => {
    if (!flyoutOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!asideRef.current?.contains(e.target as Node)) setOpenMenus([]);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) setOpenMenus([]);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [flyoutOpen]);

  return (
    <m.aside
      ref={asideRef}
      initial={false}
      animate={{
        width: collapsed ? 56 : width,
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 35,
      }}
      className={clsx(
        `
        fixed md:relative top-16 md:top-0 left-0 z-50 md:z-30
        h-[calc(100vh-64px)] md:h-full
        bg-[rgb(var(--card))]
        border-r border-[rgb(var(--border))]
        flex flex-col transition-transform duration-300
        overflow-visible
        `,
        open ? "translate-x-0" : "-translate-x-full md:translate-x-0",
      )}
    >
      {/* Header */}
      <div
        className={clsx(
          "flex h-14 shrink-0 items-center border-b border-[rgb(var(--border))]",
          collapsed ? "justify-center px-2" : "justify-between px-4",
        )}
      >
        {!collapsed && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--text-muted))]">
            {t("menu")}
          </span>
        )}

        <button
          type="button"
          onClick={toggle}
          aria-label={
            collapsed
              ? t("expandSidebar", { defaultValue: "Expand sidebar" })
              : t("collapseSidebar", { defaultValue: "Collapse sidebar" })
          }
          title={
            collapsed
              ? t("expandSidebar", { defaultValue: "Expand sidebar" })
              : t("collapseSidebar", { defaultValue: "Collapse sidebar" })
          }
          className="hidden h-8 w-8 items-center justify-center rounded-lg text-[rgb(var(--text-muted))] transition-colors hover:bg-[rgb(var(--card-muted))] hover:text-[rgb(var(--text-primary))] md:flex"
        >
          {collapsed ? (
            <PanelLeftOpen size={17} />
          ) : (
            <PanelLeftClose size={17} />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav
        className="
          flex-1 flex flex-col gap-0.5 px-2 pt-3
          overflow-y-auto overflow-x-visible
          scrollbar-none
          [&::-webkit-scrollbar]:hidden
          [-ms-overflow-style:none]
        "
      >
        {filteredNav.map((item) => {
          const Icon = item.icon;
          const isMenuOpen = openMenus.includes(item.path);
          const isActive = isChildRouteActive(item);

          if (item.children) {
            return (
              <div key={item.path}>
                <button
                  ref={(el) => {
                    topButtonRefs.current[item.path] = el;
                  }}
                  onClick={(e) => toggleMenu(item.path, e)}
                  aria-expanded={isMenuOpen}
                  className={clsx(
                    "group relative flex h-10 w-full items-center rounded-xl text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))] focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--card))]",
                    collapsed ? "justify-center" : "justify-between px-3",
                    isActive
                      ? collapsed
                        ? "bg-[rgb(var(--text-accent))]/10 text-[rgb(var(--text-accent))]"
                        : "text-[rgb(var(--text-primary))]"
                      : isMenuOpen
                        ? "text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--card-muted))]"
                        : "text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--card-muted))] hover:text-[rgb(var(--text-primary))]",
                  )}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Icon
                      size={18}
                      className={clsx(
                        "shrink-0 transition-colors",
                        isActive
                          ? "text-[rgb(var(--text-accent))]"
                          : "text-[rgb(var(--text-muted))] group-hover:text-[rgb(var(--text-secondary))]",
                      )}
                    />
                    {!collapsed && (
                      <TruncatedText text={t(item.labelKey as any)} />
                    )}
                  </div>

                  {!collapsed && (
                    <m.span
                      animate={{ rotate: isMenuOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-[rgb(var(--text-muted))]"
                    >
                      <NAV_ICONS.chevron size={14} />
                    </m.span>
                  )}

                  {collapsed && (
                    <CollapsedTooltip
                      label={(t as (key: string) => string)(item.labelKey)}
                    />
                  )}
                </button>

                <AnimatePresence initial={false}>
                  {isMenuOpen && (
                    <m.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      style={
                        collapsed && flyoutTop !== null
                          ? {
                              top: flyoutTop,
                              maxHeight: `calc(100% - ${flyoutTop}px - 16px)`,
                            }
                          : undefined
                      }
                      className={clsx(
                        collapsed
                          ? "fixed left-[60px] z-50 flex w-60 flex-col gap-0.5 overflow-y-auto rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-1.5 shadow-xl"
                          : "mb-1 ml-[21px] mt-0.5 flex flex-col gap-0.5 overflow-hidden border-l border-[rgb(var(--border))] pl-2",
                      )}
                    >
                      {collapsed && (
                        <div className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-[rgb(var(--text-muted))]">
                          {t(item.labelKey as any)}
                        </div>
                      )}
                      <NavChildren
                        items={item.children}
                        collapsed={collapsed}
                        openMenus={openMenus}
                        toggleMenu={toggleMenu}
                        location={location}
                        t={t as (key: string) => string}
                      />
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            );
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                clsx(
                  // shrink-0: the nav is a scrolling flex column — without it,
                  // once open groups overflow the sidebar these links are
                  // squashed below h-10 instead of the list scrolling.
                  "group relative flex h-10 shrink-0 items-center gap-3 rounded-xl text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))] focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--card))]",
                  collapsed ? "justify-center" : "px-3",
                  isActive
                    ? "bg-[rgb(var(--accent-fill))] text-[rgb(var(--accent-ink))] shadow-sm shadow-[rgb(var(--text-accent))]/30"
                    : "text-[rgb(var(--text-secondary))] hover:bg-[rgb(var(--card-muted))] hover:text-[rgb(var(--text-primary))]",
                )
              }
            >
              {({ isActive }) => {
                const badge =
                  item.path === "/notifications" && unreadCount > 0
                    ? unreadCount > 99
                      ? "99+"
                      : String(unreadCount)
                    : null;
                return (
                  <>
                    <span
                      className={clsx(
                        "relative flex shrink-0 transition-colors",
                        !isActive &&
                          "text-[rgb(var(--text-muted))] group-hover:text-[rgb(var(--text-secondary))]",
                      )}
                    >
                      <Icon size={18} />
                      {badge && collapsed && (
                        <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-[rgb(var(--card))]" />
                      )}
                    </span>

                    {!collapsed && (
                      <TruncatedText
                        text={t(item.labelKey as any)}
                        className="flex-1"
                      />
                    )}

                    {badge && !collapsed && (
                      <span
                        className={clsx(
                          "ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
                          isActive
                            ? "bg-white/25 text-white"
                            : "bg-red-600 text-white",
                        )}
                      >
                        {badge}
                      </span>
                    )}

                    {collapsed && (
                      <CollapsedTooltip
                        label={
                          badge
                            ? `${t(item.labelKey as any)} (${badge})`
                            : t(item.labelKey as any)
                        }
                      />
                    )}
                  </>
                );
              }}
            </NavLink>
          );
        })}
        {/* Breathing room under the last item. A spacer rather than bottom
            padding: a scrolling flex column does not reliably count padding
            in its scroll height, so the last item still ended flush. */}
        <div aria-hidden className="h-4 shrink-0" />
      </nav>

      {/* Sign out, pinned to the foot of the menu: always in the same place,
          whatever the menu above it holds. Asks first, as the profile
          panel's sign-out does. */}
      <div className="shrink-0 border-t border-[rgb(var(--border))] p-2">
        <button
          type="button"
          onClick={() => setConfirmLogout(true)}
          aria-label={
            collapsed ? tAuth("userDropdown.buttons.logout") : undefined
          }
          className={clsx(
            "group relative flex h-10 w-full items-center gap-3 rounded-xl text-[13px] font-medium text-[rgb(var(--text-secondary))] transition-colors hover:bg-rose-500/10 hover:text-rose-700 dark:hover:text-rose-300" +
              " focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))] focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--card))]",
            collapsed ? "justify-center" : "px-3",
          )}
        >
          <LogOut
            size={18}
            className="shrink-0 text-[rgb(var(--text-muted))] transition-colors group-hover:text-current"
          />
          {!collapsed && (
            <span className="truncate">
              {tAuth("userDropdown.buttons.logout")}
            </span>
          )}
          {collapsed && (
            <CollapsedTooltip
              anchored
              label={tAuth("userDropdown.buttons.logout")}
            />
          )}
        </button>
      </div>

      <ConfirmModal
        open={confirmLogout}
        title={tAuth("userDropdown.logoutModal.title")}
        description={tAuth("userDropdown.logoutModal.description")}
        confirmLabel={tAuth("userDropdown.logoutModal.confirmLabel")}
        variant="danger"
        onCancel={() => setConfirmLogout(false)}
        onConfirm={async () => {
          setConfirmLogout(false);
          logout();
        }}
      />
    </m.aside>
  );
}
